import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";
import { Copy, Share2, Users, Gift, Check } from "lucide-react";

export function ReferralCard() {
  const user = useUser();
  const { toast } = useToast();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [convertedCount, setConvertedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const AMBASSADOR_GOAL = 10;
  const progress = Math.min((convertedCount / AMBASSADOR_GOAL) * 100, 100);
  const referralLink = referralCode ? `https://datahireclub.com.br/r/${referralCode}` : null;

  useEffect(() => {
    if (user) {
      fetchReferralData();
    }
  }, [user]);

  const fetchReferralData = async () => {
    if (!user) return;

    try {
      // Buscar referral code existente
      const { data: referrals, error } = await supabase
        .from('user_referrals')
        .select('referral_code, referred_id, status')
        .eq('referrer_id', user.id);

      if (error) throw error;

      if (referrals && referrals.length > 0) {
        // Pegar o primeiro código (todos têm o mesmo código por usuário)
        const code = referrals[0].referral_code;
        setReferralCode(code);

        // Contar conversões
        const converted = referrals.filter(r => r.status === 'converted' && r.referred_id).length;
        setConvertedCount(converted);
      }
    } catch (error) {
      console.error('Erro ao buscar dados de referral:', error);
    }
  };

  const generateReferralCode = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const { data, error } = await supabase.rpc('create_user_referral', {
        p_user_id: user.id
      });

      if (error) throw error;

      setReferralCode(data);
      toast({
        title: "Link criado!",
        description: "Seu link de convite foi gerado com sucesso.",
      });
    } catch (error) {
      console.error('Erro ao gerar código:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar o link de convite.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!referralLink) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast({
        title: "Copiado!",
        description: "Link copiado para a área de transferência.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível copiar o link.",
        variant: "destructive",
      });
    }
  };

  const shareWhatsApp = () => {
    if (!referralLink) return;
    const message = encodeURIComponent(
      `Quer entrar na melhor comunidade de dados do Brasil? 🚀\n\nUse meu link de convite: ${referralLink}`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const shareLinkedIn = () => {
    if (!referralLink) return;
    const url = encodeURIComponent(referralLink);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  return (
    <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Gift className="h-5 w-5 text-primary" />
          Convide Amigos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!referralCode ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Convide amigos para a comunidade e ganhe a conquista <strong>Embaixador</strong>!
            </p>
            <Button 
              onClick={generateReferralCode} 
              disabled={loading}
              className="w-full"
            >
              <Users className="mr-2 h-4 w-4" />
              {loading ? "Gerando..." : "Gerar Meu Link de Convite"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Link de convite */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">
                Seu link de convite
              </label>
              <div className="flex gap-2">
                <div className="flex-1 bg-background rounded-md px-3 py-2 text-sm border truncate">
                  {referralLink}
                </div>
                <Button 
                  size="icon" 
                  variant="outline" 
                  onClick={copyToClipboard}
                  className="shrink-0"
                >
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Botões de compartilhamento */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={shareWhatsApp}
                className="flex-1"
              >
                <Share2 className="mr-2 h-4 w-4" />
                WhatsApp
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={shareLinkedIn}
                className="flex-1"
              >
                <Share2 className="mr-2 h-4 w-4" />
                LinkedIn
              </Button>
            </div>

            {/* Progresso para conquista */}
            <div className="space-y-2 pt-2 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Progresso: Embaixador</span>
                <span className="font-medium">{convertedCount}/{AMBASSADOR_GOAL}</span>
              </div>
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {convertedCount === 0 
                  ? "Convide amigos para começar!" 
                  : convertedCount >= AMBASSADOR_GOAL 
                    ? "🎉 Parabéns! Você é um Embaixador!"
                    : `Faltam ${AMBASSADOR_GOAL - convertedCount} convites para a conquista!`
                }
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
